import React from "react";
import { Link } from "react-router-dom";
import { capitalize, filter, find, has, toLower } from "lodash";
import { Box, Flex } from "../../components/Flex";
import { Content } from "../../components/Layout";
import { FilledLabel, Label, Paragraph, Title } from "../../components/Text";
import { CloseIcon } from "../../icons/CloseIcon";
import { capitalizeFirstLetter, fixPackageSize, formatDate, getTitle } from "../../utils";
import { renderFieldContent } from "../../components/Layout";
import { PACKAGE_FIELDS, PERMISSIONS_FIELDS } from "../../settings";
import { PackageAuditList } from "./PackageAuditList";

const AuditDetails = ({ tab, data, onClose, getFullPackageAudit, selected, loading }) => {
    const getField = (key, value, attrList=[]) => {
        let field = find(attrList, { key: key });
        if (value == null) {
            return "None"
        };
        if (has(field, "render")) {
            return field.render(value);
        }
        return value;
    }

    const fields = {
        downloads: {
            title: "Package download",
            main: [
                { label: "Package name", attr: "packageName", render: (d, k) => <Link to={`/packages/${toLower(k.packageNumber)}`}>{d}</Link> },
                { label: "File name", attr: "saveAs"},
                { label: "Version", attr: "version" },
                { label: "System Architecture", attr: "sysarch", render: (d) => (
                    <Flex gap={{ right: 8 }}>
                        {d.map(sysarch => (
                            <FilledLabel key={sysarch}>{sysarch}</FilledLabel>
                        ))}
                    </Flex>
                )},
            ],
            box: [
                { label: "Downloaded By", attr: "fullName" },
                { label: "Email", attr: "email", render: d => <Link to={`/users/${toLower(d)}`}>{d}</Link> },
                { label: "Organization", attr: "orgName" },
                { label: "Download Date", attr: "downloadDatetime", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") }
            ]
        },
        packages: {
            title: "Package",
            main: [
                { attr: "packageName", render: (d, k) => k.status == "file_uploaded"
                                                            ? <>The following file has been added to the <b>{d}</b> package:</>
                                                            :  k.status == "file_deleted"
                                                                ? <>The following file has been removed from the <b>{d}</b> package:</>
                                                                : <>The <b>{d}</b> package has been {k.status}{k.status === "registered" ? " with the following values:" : "."}</>},
                { attr: "saveAs", renderUndefined: false, render: (d) => (
                                        <Flex gap={{ right: 3 }} align="baseline">
                                            <Paragraph color="var(--sp-label-color)" lineHeight="22px" whiteSpace="nowrap">File name:</Paragraph>
                                            <Paragraph>{d}</Paragraph>
                                        </Flex>)},
                { attr: "downloadSize", renderUndefined: false, render: (d) => (
                                        <Flex gap={{ right: 3 }} align="baseline">
                                            <Paragraph color="var(--sp-label-color)" lineHeight="22px" whiteSpace="nowrap">File size:</Paragraph>
                                            <Paragraph>{fixPackageSize(d)}</Paragraph>
                                        </Flex>)},
                { attr: "updatedFields", renderUndefined: false, render: (d, k) => (
                        <Flex direction="column" gap={{ bottom: 12 }}>
                            {k.status === "updated"
                                ? filter(Object.keys(d), key => !key.startsWith("old")).sort().map(key => (
                                    <Flex key={key} gap={{ right: 12 }}>
                                        <Label size={14}>{getTitle(key, PACKAGE_FIELDS)}:</Label>
                                        <Flex gap={{ right: 12 }}>
                                            <Paragraph color="var(--sp-error-color)" style={{ textDecoration: "line-through" }}>{getField(key, d["old"+key], PACKAGE_FIELDS)}</Paragraph>
                                            <span>&#8594;</span>
                                            <Paragraph color="var(--sp-success-color)">{getField(key, d[key], PACKAGE_FIELDS)}</Paragraph>
                                        </Flex>
                                    </Flex>
                                ))
                                : Object.keys(d).reverse().map(key => (
                                    <Flex key={key} gap={{ right: 12 }}>
                                        <Label size={14}>{getTitle(key, PACKAGE_FIELDS)}:</Label>
                                        <Paragraph>{getField(key, d[key], PACKAGE_FIELDS)}</Paragraph>
                                    </Flex>
                                ))
                            }
                        </Flex>
                    )
                },
                { attr: "comment", renderUndefined: false, render: (d) => (
                        <Flex key={"verify_comment"} gap={{ right: 12 }}>
                            <Label size={14}>cause:</Label>
                            <Paragraph>{d}</Paragraph>
                        </Flex>
                    )
                }
            ],
            box: [
                { label: "Done By", attr: "fullName" },
                { label: "Email", attr: "email", render: d => <Link to={`/users/${toLower(d)}`}>{d}</Link> },
                { label: "Activity Date", attr: "activityDatetime", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") }
            ]
        },
        users: {
            title: "User",
            main: [
                { label: "Email", attr: "email" },
                { label: "User Name", attr: "fullName" },
                { label: "Organization", attr: "orgName" },
                { label: "Status", attr: "status", render: (d) => capitalize(d)},
            ],
            box: [
                { label: "Updated By", attr: "updatedBy"},
                { label: "Update Date", attr: "updateDatetime", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") }
            ]
        },
        grants: {
            title: "User grant",
            main: [
                { label: "Email", attr: "email" },
                { label: "User Name", attr: "fullName" },
                { label: "Organization", attr: "orgName" },
                { label: "Grant", attr: "grantName" },
                { label: "Status", attr: "status" },
                { label: "Expiration date", attr: "expiryDate" }
            ],
            box: [
                { label: "Updated By", attr: "updatedBy"},
                { label: "Update Date", attr: "updateDatetime", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") }
            ]
        },
        impersonations: {
            title: "Impersonation",
            main: [
                { label: "Email", attr: "email" },
                { label: "User Name", attr: "fullName" },
                { label: "Organization", attr: "orgImpersonateName" },
            ],
            box: [
                { label: "Impersonation started", attr: "startDate", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") },
                { label: "Impersonation dropped", attr: "endDate", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") }
            ]
        },
        contracts: {
            title: "Organization contract",
            main: [
                { label: "Contract", attr: "contractName" },
                { label: "Organization", attr: "orgName", render: (d, record) => <Link to={`/organisations/${record["orgAlias"]}`}>{d}</Link>},
                { label: "Status", attr: "action" },
                { label: "Expiration date", attr: "endDate" }
            ],
            box: [
                { label: "Updated By", attr: "fullName", render: (d, record) => d + " <" + record["updatedBy"] + ">"  },
                { label: "Update Date", attr: "updatedDatetime", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") }
            ]
        },
        orgs: {
            title: "Organization",
            main: [
                { label: "Organization", attr: "orgName", render: (d, record) => <Link to={`/organisations/${record["orgAlias"]}`}>{d}</Link>},
                { label: "Status", attr: "status" },
                { attr: "updatedFields", renderUndefined: false, render: (d, k) => (
                    <Flex direction="column" gap={{ bottom: 12 }}>
                        {k.status === "updated"
                            ? filter(Object.keys(d), key => !key.startsWith("old")).sort().map(key => (
                                <Flex key={key} gap={{ right: 12 }}>
                                    <Label size={14}>{getTitle(key, PACKAGE_FIELDS)}:</Label>
                                    <Flex gap={{ right: 12 }}>
                                        <Paragraph color="var(--sp-error-color)" style={{ textDecoration: "line-through" }}>{getField(key, d["old"+capitalizeFirstLetter(key)])}</Paragraph>
                                        <span>&#8594;</span>
                                        <Paragraph color="var(--sp-success-color)">{getField(key, d[key])}</Paragraph>
                                    </Flex>
                                </Flex>
                            ))
                            : Object.keys(d).reverse().map(key => (
                                <Flex key={key} gap={{ right: 12 }}>
                                    <Label size={14}>{getTitle(key, PACKAGE_FIELDS)}:</Label>
                                    <Paragraph>{d[key]}</Paragraph>
                                </Flex>
                            ))
                        }
                    </Flex>)
                }
            ],
            box: [
                { label: "Updated By", attr: "fullName", render: (d, record) => d + " <" + record["email"] + ">"  },
                { label: "Update Date", attr: "activityDatetime", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") }
            ]
        },
        permissions: {
            title: "Package Permissions",
            main: [
                { attr: "packageName", render: (d, record) => <>The <b>{d}</b> package permissions have been {record.deleted ? "deleted." : "updated with the following values:"}</>},
                { attr: "updatedFields", renderUndefined: false, render: (d, k) => (
                    <Flex direction="column" gap={{ bottom: 12 }}>
                        {filter(Object.keys(d), key => !key.startsWith("old")).sort().map(key => (
                            <Flex key={key} gap={{ right: 12 }}>
                                <Label size={14}>{getTitle(key, PERMISSIONS_FIELDS)}:</Label>
                                <Flex gap={{ right: 12 }}>
                                    <Paragraph color="var(--sp-error-color)" style={{ textDecoration: "line-through" }}>{getField(key, d["old"+capitalizeFirstLetter(key)], PERMISSIONS_FIELDS)}</Paragraph>
                                    <span>&#8594;</span>
                                    <Paragraph color="var(--sp-success-color)">{getField(key, d[key], PERMISSIONS_FIELDS)}</Paragraph>
                                </Flex>
                            </Flex>
                        ))}
                    </Flex>)
                }
            ],
            box: [
                { label: "Updated By", attr: "fullName", render: (d, record) => d + " <" + record["email"] + ">"  },
                { label: "Update Date", attr: "activityDatetime", render: d => formatDate(d, "DD-MM-YYYY HH:mm:ss") }
            ]
        }
    };


    return(
        Object.keys(fields).includes(tab)
            ? <Content background="#FFFFFF" gap={{ bottom: 18 }} scrollable>
                <Flex width="100%" justify="space-between" margin={{ bottom: "24px"}}>
                    <Title h4>{fields[tab].title} audit details</Title>
                    <CloseIcon onClick={onClose} />
                </Flex>
                <Flex direction="column" gap={{ bottom: 16 }}>
                    <Flex id="audit-details-content" justify="space-between" gap={{ right: 18 }}>
                        <Flex direction="column" gap={{ bottom: 16 }}>
                            {renderFieldContent(fields[tab].main, data, loading)}
                        </Flex>
                        <Box flatten background="#F0F4FB" gap={{ bottom: 8 }}>
                            {renderFieldContent(fields[tab].box, data, loading)}
                        </Box>
                    </Flex>
                </Flex>
                {tab === "packages" && <PackageAuditList getFullPackageAudit={() => getFullPackageAudit(data)} data={data.fullAudit} selected={selected} loading={loading} />}
            </Content>
            : null
    )
};

export { AuditDetails };